/**
 * Main hook for warehouse table functionality
 * Combines all table operations in a single, easy-to-use hook
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import type { 
  TableDataRow, 
  ColumnDefinition, 
  TableConfig, 
  TableState, 
  SortDirection,
  FilterState,
  SummaryCalculation,
  RowSelectionState,
  RowSelectionConfig
} from '../types/table';
import { 
  sortData, 
  filterData, 
  paginateData, 
  calculateSummary, 
  exportToCSV,
  saveTableConfig,
  loadTableConfig 
} from '../utils/tableUtils';

interface UseWarehouseTableProps<T extends TableDataRow> {
  data: T[];
  columns: ColumnDefinition<T>[];
  config?: Partial<TableConfig>;
  summaryCalculations?: SummaryCalculation<T>[];
  
  // Row Selection (Hybrid approach)
  selectedRows?: (string | number)[]; // External control
  onSelectionChange?: (selectedRows: (string | number)[], selectedData: T[]) => void;
  getRowId?: (row: T) => string | number; // Custom ID extraction
}

interface UseWarehouseTableReturn<T extends TableDataRow> {
  // State
  tableState: TableState<T>;
  
  // Actions
  handleSort: (column: keyof T) => void;
  handleFilter: (filters: Partial<FilterState>) => void;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
  handleColumnToggle: (columnId: string) => void;
  handleColumnReorder: (fromIndex: number, toIndex: number) => void;
  handleColumnResize: (columnId: string, width: number) => void;
  handleExport: () => void;
  handleReset: () => void;
  
  // Row Selection Actions
  handleRowSelect: (rowId: string | number, selected: boolean) => void;
  handleSelectAll: (selected: boolean) => void;
  handleSelectionClear: () => void;
  
  // Summary data
  summaryData: Record<string, number>;
  
  // Configuration
  visibleColumns: ColumnDefinition<T>[];
}

const DEFAULT_CONFIG: TableConfig = {
  showPagination: true,
  showColumnConfig: true,
  showGlobalSearch: true,
  showFilters: true,
  showExport: false,
  showSummary: false,
  defaultPageSize: 50,
  allowColumnReorder: true,
  allowColumnResize: true,
  allowRowSelection: false,
  stickyHeader: true,
  stickyFooter: false,
  compactMode: true
};

export const useWarehouseTable = <T extends TableDataRow>({
  data,
  columns: initialColumns,
  config: userConfig = {},
  summaryCalculations = [],
  selectedRows: externalSelectedRows,
  onSelectionChange,
  getRowId
}: UseWarehouseTableProps<T>): UseWarehouseTableReturn<T> => {
  
  const config = { ...DEFAULT_CONFIG, ...userConfig };
  
  // Core state
  const [columns, setColumns] = useState<ColumnDefinition<T>[]>(initialColumns);
  const [sortColumn, setSortColumn] = useState<keyof T | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [filters, setFilters] = useState<FilterState>({
    globalSearch: '',
    columnFilters: {},
    dropdownFilters: {}
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(config.defaultPageSize || 50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  // Default row ID function
  const defaultGetRowId = useCallback((row: T): string | number => {
    return row.id || (row as any).itemNo || JSON.stringify(row);
  }, []);

  const effectiveGetRowId = getRowId || defaultGetRowId;

  // Row selection configuration
  const selectionConfig: RowSelectionConfig = useMemo(() => {
    const allowSelection = config.allowRowSelection;
    if (typeof allowSelection === 'object') {
      return allowSelection;
    }
    return {
      mode: 'multi',
      selectAllMode: 'all-data',
      getRowId: effectiveGetRowId
    };
  }, [config.allowRowSelection, effectiveGetRowId]);

  // Row selection state (internal or external)
  const isControlledSelection = externalSelectedRows !== undefined;
  const [internalSelectedRows, setInternalSelectedRows] = useState<Set<string | number>>(new Set());
  
  const selectedRowsSet = useMemo(() => {
    if (isControlledSelection) {
      return new Set(externalSelectedRows);
    }
    return internalSelectedRows;
  }, [isControlledSelection, externalSelectedRows, internalSelectedRows]);

  // Load saved configuration on mount
  useEffect(() => {
    if (config.persistenceKey) {
      const savedConfig = loadTableConfig(config.persistenceKey);
      if (savedConfig) {
        // Apply saved column configuration
        const updatedColumns = columns.map(col => {
          const savedCol = savedConfig.columns.find((c: any) => c.id === col.id);
          if (savedCol) {
            return {
              ...col,
              visible: savedCol.visible,
              width: savedCol.width
            };
          }
          return col;
        });
        
        // Reorder columns based on saved order
        const orderedColumns = savedConfig.columns
          .sort((a: any, b: any) => a.order - b.order)
          .map((savedCol: any) => updatedColumns.find(col => col.id === savedCol.id))
          .filter(Boolean);
        
        // Add any new columns that weren't in saved config
        const newColumns = updatedColumns.filter(col => 
          !savedConfig.columns.some((c: any) => c.id === col.id)
        );
        
        setColumns([...orderedColumns, ...newColumns] as ColumnDefinition<T>[]);
        
        if (savedConfig.pageSize) {
          setPageSize(savedConfig.pageSize);
        }
      }
    }
  }, [config.persistenceKey]);

  // Visible columns
  const visibleColumns = useMemo(() => 
    columns.filter(col => col.visible), 
    [columns]
  );

  // Apply sorting and filtering
  const processedData = useMemo(() => {
    let result = [...data];

    // Apply filtering
    result = filterData(
      result,
      visibleColumns,
      filters.columnFilters || {},
      filters.dropdownFilters || {},
      filters.globalSearch
    );

    // Apply sorting
    result = sortData(result, sortColumn, sortDirection);

    return result;
  }, [data, visibleColumns, filters, sortColumn, sortDirection]);

  // Apply pagination
  const { paginatedData, totalPages } = useMemo(() => 
    paginateData(processedData, currentPage, pageSize),
    [processedData, currentPage, pageSize]
  );

  // Reset to first page when data changes
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Calculate summaries
  const summaryData = useMemo(() => {
    const summaries: Record<string, number> = {};
    
    summaryCalculations.forEach(calc => {
      const columnKey = String(calc.column);
      if (calc.type === 'custom' && calc.customCalculation) {
        summaries[columnKey] = calc.customCalculation(processedData);
      } else {
        summaries[columnKey] = calculateSummary(processedData, calc.column, calc.type as 'sum' | 'average' | 'count' | 'min' | 'max');
      }
    });
    
    return summaries;
  }, [processedData, summaryCalculations]);

  // Save configuration when columns or pageSize change
  useEffect(() => {
    if (config.persistenceKey) {
      saveTableConfig(config.persistenceKey, columns, pageSize);
    }
  }, [columns, pageSize, config.persistenceKey]);

  // Action handlers
  const handleSort = useCallback((column: keyof T) => {
    if (sortColumn === column) {
      // Cycle through: asc -> desc -> none
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      } else {
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page on sort
  }, [sortColumn, sortDirection]);

  const handleFilter = useCallback((newFilters: Partial<FilterState>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to first page on filter
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  const handleColumnToggle = useCallback((columnId: string) => {
    setColumns(prev => prev.map(col => 
      col.id === columnId ? { ...col, visible: !col.visible } : col
    ));
  }, []);

  const handleColumnReorder = useCallback((fromIndex: number, toIndex: number) => {
    setColumns(prev => {
      const newColumns = [...prev];
      const [movedColumn] = newColumns.splice(fromIndex, 1);
      newColumns.splice(toIndex, 0, movedColumn);
      return newColumns;
    });
  }, []);

  const handleColumnResize = useCallback((columnId: string, width: number) => {
    setColumns(prev => prev.map(col => 
      col.id === columnId ? { ...col, width: Math.max(col.minWidth || 60, width) } : col
    ));
  }, []);

  const handleExport = useCallback(() => {
    const filename = `${config.persistenceKey || 'warehouse-data'}-${new Date().toISOString().split('T')[0]}.csv`;
    exportToCSV(processedData, visibleColumns, filename);
  }, [processedData, visibleColumns, config.persistenceKey]);

  const handleReset = useCallback(() => {
    setColumns(initialColumns);
    setSortColumn(null);
    setSortDirection(null);
    setFilters({
      globalSearch: '',
      columnFilters: {},
      dropdownFilters: {}
    });
    setCurrentPage(1);
    setPageSize(config.defaultPageSize || 50);
    
    // Clear saved configuration
    if (config.persistenceKey) {
      localStorage.removeItem(`warehouse-table-${config.persistenceKey}`);
    }
  }, [initialColumns, config.persistenceKey, config.defaultPageSize]);

  // Row Selection Handlers
  const handleRowSelect = useCallback((rowId: string | number, selected: boolean) => {
    let newSelectedRows: Set<string | number>;
    
    if (selectionConfig.mode === 'single') {
      newSelectedRows = selected ? new Set([rowId]) : new Set();
    } else {
      newSelectedRows = new Set(selectedRowsSet);
      if (selected) {
        newSelectedRows.add(rowId);
      } else {
        newSelectedRows.delete(rowId);
      }
    }

    // Update internal state if uncontrolled
    if (!isControlledSelection) {
      setInternalSelectedRows(newSelectedRows);
    }

    // Notify parent of change
    if (onSelectionChange) {
      const selectedRowsArray = Array.from(newSelectedRows);
      const selectedData = data.filter(row => newSelectedRows.has(effectiveGetRowId(row)));
      onSelectionChange(selectedRowsArray, selectedData);
    }
  }, [selectedRowsSet, selectionConfig.mode, isControlledSelection, onSelectionChange, data, effectiveGetRowId]);

  const handleSelectAll = useCallback((selected: boolean) => {
    let targetData: T[];
    
    if (selectionConfig.selectAllMode === 'all-data') {
      targetData = processedData; // All filtered data
    } else {
      targetData = paginatedData; // Current page only
    }

    const newSelectedRows = new Set(selectedRowsSet);
    
    if (selected) {
      targetData.forEach(row => {
        newSelectedRows.add(effectiveGetRowId(row));
      });
    } else {
      targetData.forEach(row => {
        newSelectedRows.delete(effectiveGetRowId(row));
      });
    }

    // Update internal state if uncontrolled
    if (!isControlledSelection) {
      setInternalSelectedRows(newSelectedRows);
    }

    // Notify parent of change
    if (onSelectionChange) {
      const selectedRowsArray = Array.from(newSelectedRows);
      const selectedData = data.filter(row => newSelectedRows.has(effectiveGetRowId(row)));
      onSelectionChange(selectedRowsArray, selectedData);
    }
  }, [selectedRowsSet, selectionConfig.selectAllMode, processedData, paginatedData, isControlledSelection, onSelectionChange, data, effectiveGetRowId]);

  const handleSelectionClear = useCallback(() => {
    const newSelectedRows = new Set<string | number>();
    
    // Update internal state if uncontrolled
    if (!isControlledSelection) {
      setInternalSelectedRows(newSelectedRows);
    }

    // Notify parent of change
    if (onSelectionChange) {
      onSelectionChange([], []);
    }
  }, [isControlledSelection, onSelectionChange]);

  // Calculate selection state for UI
  const selectedData = useMemo(() => {
    return data.filter(row => selectedRowsSet.has(effectiveGetRowId(row)));
  }, [data, selectedRowsSet, effectiveGetRowId]);

  const allCurrentPageSelected = useMemo(() => {
    return paginatedData.length > 0 && paginatedData.every(row => selectedRowsSet.has(effectiveGetRowId(row)));
  }, [paginatedData, selectedRowsSet, effectiveGetRowId]);

  const someCurrentPageSelected = useMemo(() => {
    return paginatedData.some(row => selectedRowsSet.has(effectiveGetRowId(row)));
  }, [paginatedData, selectedRowsSet, effectiveGetRowId]);

  const selectionState: RowSelectionState<T> = {
    selectedRows: selectedRowsSet,
    isSelectAllChecked: allCurrentPageSelected,
    isSelectAllIndeterminate: someCurrentPageSelected && !allCurrentPageSelected,
    selectedData
  };

  // Table state
  const tableState: TableState<T> = {
    data,
    filteredData: processedData,
    paginatedData,
    columns,
    visibleColumns,
    filters,
    sort: { column: sortColumn, direction: sortDirection },
    pagination: {
      currentPage,
      pageSize,
      totalRows: processedData.length,
      totalPages
    },
    selection: selectionState,
    loading,
    error
  };

  return {
    tableState,
    handleSort,
    handleFilter,
    handlePageChange,
    handlePageSizeChange,
    handleColumnToggle,
    handleColumnReorder,
    handleColumnResize,
    handleExport,
    handleReset,
    handleRowSelect,
    handleSelectAll,
    handleSelectionClear,
    summaryData,
    visibleColumns
  };
};